import { Tag } from 'antd'
import {
  EHorseStatus,
  HORSE_ADMIN_UNPUBLISHED_REASON,
  HORSE_EXPIRED_REASON,
  IPopulatedHorseWithoutCount,
  isHorseSoldReason
} from 'models/horse'

interface IHorseStatusItemData {
  key: EHorseStatus,
  label: React.ReactNode
}

export const horseStatuses = {
  [EHorseStatus.MODERATION]: {
    value: EHorseStatus.MODERATION,
    label: 'На модерации',
    color: 'geekblue'
  },
  [EHorseStatus.PUBLISHED]: {
    value: EHorseStatus.PUBLISHED,
    label: 'Опубликована',
    color: 'green'
  },
  [EHorseStatus.REJECTED]: {
    value: EHorseStatus.REJECTED,
    label: 'Отклонена',
    color: 'red'
  },
  [EHorseStatus.ARCHIVED]: {
    value: EHorseStatus.ARCHIVED,
    label: 'Снята с публикации',
    color: 'default'
  }
}

// Истёкшая и не менявшаяся после проверки лошадь публикуется сразу, остальные уходят на модерацию
export const getRepublishMessage = (status: EHorseStatus) =>
  status === EHorseStatus.PUBLISHED
    ? 'Лошадь снова опубликована на 30 дней'
    : 'Заявка отправлена на модерацию'

const UNPUBLISHED_NOTES: Record<string, string> = {
  [HORSE_EXPIRED_REASON]: 'Истёк срок размещения',
  [HORSE_ADMIN_UNPUBLISHED_REASON]: 'Снята администратором'
}

export interface IHorseStatusView {
  label: string
  color: string
  note?: string
}

// Снятая с публикации с ответом на «Где вы продали лошадь?» — «Продана», комментарий — где продали
export const getHorseStatusView = ({ status, archived_reason }: Pick<IPopulatedHorseWithoutCount, 'status' | 'archived_reason'>): IHorseStatusView => {
  if (status === EHorseStatus.ARCHIVED && isHorseSoldReason(archived_reason)) {
    return { label: 'Продана', color: 'purple', note: archived_reason }
  }
  const { label, color } = horseStatuses[status]
  return status === EHorseStatus.ARCHIVED
    ? { label, color, note: UNPUBLISHED_NOTES[archived_reason ?? ''] }
    : { label, color }
}

// Сервер фильтрует по статусу, а проданная для него — та же снятая с публикации
export const horseStatusFilters = Object.values(horseStatuses).map(({ value, label }) => ({
  text: value === EHorseStatus.ARCHIVED ? 'Снята с публикации или продана' : label,
  value
}))

// Свои лошади фильтруются в браузере — по тому статусу, который владелец видит в строке
export const horseStatusViewFilters = [
  ...Object.values(horseStatuses).map(({ label }) => label),
  'Продана'
].map((label) => ({ text: label, value: label }))

const moderationStatus: IHorseStatusItemData = {
  key: EHorseStatus.MODERATION,
  label: (
    <Tag color={horseStatuses[EHorseStatus.MODERATION].color}>
      {horseStatuses[EHorseStatus.MODERATION].label}
    </Tag>
  )
}

const approvedStatus: IHorseStatusItemData = {
  key: EHorseStatus.PUBLISHED,
  label: (
    <Tag color={horseStatuses[EHorseStatus.PUBLISHED].color}>
      {horseStatuses[EHorseStatus.PUBLISHED].label}
    </Tag>
  )
}

const rejectedStatus: IHorseStatusItemData = {
  key: EHorseStatus.REJECTED,
  label: (
    <Tag color={horseStatuses[EHorseStatus.REJECTED].color}>
      {horseStatuses[EHorseStatus.REJECTED].label}
    </Tag>
  )
}

const archivedStatus: IHorseStatusItemData = {
  key: EHorseStatus.ARCHIVED,
  label: (
    <Tag color={horseStatuses[EHorseStatus.ARCHIVED].color}>
      {horseStatuses[EHorseStatus.ARCHIVED].label}
    </Tag>
  )
}

export const getHorseStatusItems = (status: EHorseStatus) => {
  if (status === EHorseStatus.MODERATION) {
    return [approvedStatus, rejectedStatus, archivedStatus]
  }
  if (status === EHorseStatus.PUBLISHED) {
    return [moderationStatus, rejectedStatus, archivedStatus]
  }
  if (status === EHorseStatus.REJECTED) {
    return [moderationStatus, approvedStatus, archivedStatus]
  }
  if (status === EHorseStatus.ARCHIVED) {
    return [moderationStatus, approvedStatus]
  }
  return []
}
